import React, { useState, useCallback, useEffect } from 'react';
import { Search, Plus, Trash2, AlertCircle, Package, X, Layers, Box, Building2, Star } from 'lucide-react';
import purchasesService from '../services/purchasesService';
import inventoryService from '../services/inventoryService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const PurchaseItemsGrid = ({ items, setItems, errors, onValidationChange, disabled = false, supplierId = null }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState(null); // 'variant' or 'product'

  // Quick add variant form
  const [variantForm, setVariantForm] = useState({
    product_id: '',
    sku_variacao: '',
    cor: '',
    tamanho_label: '',
    medida_text: '',
    espessura_mm: '',
    comprimento_cm: '',
    largura_cm: '',
    preco_custo: ''
  });

  // Quick add product form
  const [productForm, setProductForm] = useState({
    nome: '',
    sku: '',
    categoria_id: '',
    marca_id: '',
    unidade_id: ''
  });

  const [selectedProduct, setSelectedProduct] = useState(null);

  // Queries for dropdown data
  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories', { ativo: true }],
    queryFn: () => inventoryService.getCategories({ ativo: true, limit: 100 })
  });

  const { data: brandsData } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: inventoryService.getBrands
  });

  const { data: unitsData } = useQuery({
    queryKey: ['inventory-units'],
    queryFn: inventoryService.getUnits
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-for-variant'],
    queryFn: () => inventoryService.getProducts({ ativo: true })
  });

  // Mutation for creating variant
  const createVariantMutation = useMutation({
    mutationFn: ({ productId, data }) => inventoryService.createVariant(productId, data),
    onSuccess: (response) => {
      toast.success('Variante criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['products-for-variant'] });
      if (response?.data?.id) {
        handleSelectProduct({
          id: response.data.id,
          produto_id: response.data.produto_id,
          nome: selectedProduct?.nome || 'Novo Produto',
          sku_variacao: variantForm.sku_variacao,
          cor: variantForm.cor,
          tamanho_label: variantForm.tamanho_label,
          medida_text: variantForm.medida_text,
          preco_custo: variantForm.preco_custo
        });
      }
      resetQuickAddForms();
      setShowQuickAdd(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar variante');
    }
  });

  // Mutation for creating product
  const createProductMutation = useMutation({
    mutationFn: (data) => inventoryService.createProduct(data),
    onSuccess: (response) => {
      toast.success('Produto criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['products-for-variant'] });
      if (response?.data?.id) {
        const newProduct = response.data;
        setSelectedProduct(newProduct);
        setVariantForm(prev => ({ ...prev, product_id: newProduct.id.toString() }));
        setQuickAddMode('variant');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar produto');
    }
  });

  const resetQuickAddForms = () => {
    setVariantForm({
      product_id: '',
      sku_variacao: '',
      cor: '',
      tamanho_label: '',
      medida_text: '',
      espessura_mm: '',
      comprimento_cm: '',
      largura_cm: '',
      preco_custo: ''
    });
    setProductForm({
      nome: '',
      sku: '',
      categoria_id: '',
      marca_id: '',
      unidade_id: ''
    });
    setSelectedProduct(null);
    setQuickAddMode(null);
  };

  const handleSearch = useCallback(async (term) => {
    setSearchTerm(term);

    if (term.length < 2) {
      setSearchResults([]);
      setShowAutocomplete(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await purchasesService.searchVariants(term, supplierId);
      if (response?.data?.data) {
        setSearchResults(response.data.data.slice(0, 30));
        setShowAutocomplete(true);
      } else if (response?.data) {
        setSearchResults(Array.isArray(response.data) ? response.data.slice(0, 30) : []);
        setShowAutocomplete(true);
      }
    } catch (error) {
      console.error('Erro ao buscar variantes:', error);
    } finally {
      setIsSearching(false);
    }
  }, [supplierId]);

  const handleSelectProduct = (variant) => {
    const newItem = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      variant_id: variant.variant_id || variant.id,
      product_id: variant.product_id,
      quantity: 1,
      unit_price: parseFloat(variant.preco_custo || 0),
      supplier_price_id: variant.supplier_price_id || null,
      supplier_id: variant.fornecedor_id || null,
      supplier_name: variant.fornecedor_nome || null,
      supplier_ref: variant.referencia_fornecedor || null,
      variant: variant
    };

    // Permitir adicionar variantes com fornecedores diferentes ou alertar se a mesma oferta já estiver na lista
    const existingIndex = items.findIndex(item =>
      item.variant_id === newItem.variant_id &&
      String(item.supplier_id || '') === String(newItem.supplier_id || '')
    );
    if (existingIndex >= 0) {
      toast.error('Este item com este mesmo fornecedor já foi adicionado');
      return;
    }

    const newItems = [...items, newItem];
    setItems(newItems);
    validateItems(newItems);
    setSearchTerm('');
    setSearchResults([]);
    setShowAutocomplete(false);
  };

  const handleQuantityChange = (index, newQuantity) => {
    const qty = parseFloat(newQuantity) || 0;
    if (qty <= 0) return;

    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      quantity: qty
    };
    setItems(newItems);
    validateItems(newItems);
  };

  const handlePriceChange = (index, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    if (price < 0) return;

    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      unit_price: price
    };
    setItems(newItems);
    validateItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    validateItems(newItems);
  };

  const handleQuickAddVariant = () => {
    if (!selectedProduct && quickAddMode === 'variant') {
      toast.error('Selecione um produto primeiro');
      return;
    }

    const variantData = {
      sku_variacao: variantForm.sku_variacao || `VAR-${Date.now()}`,
      cor: variantForm.cor || null,
      tamanho_label: variantForm.tamanho_label || null,
      medida_text: variantForm.medida_text || null,
      espessura_mm: variantForm.espessura_mm ? parseFloat(variantForm.espessura_mm) : null,
      comprimento_cm: variantForm.comprimento_cm ? parseFloat(variantForm.comprimento_cm) : null,
      largura_cm: variantForm.largura_cm ? parseFloat(variantForm.largura_cm) : null,
      preco_custo: variantForm.preco_custo ? parseFloat(variantForm.preco_custo) : 0
    };

    createVariantMutation.mutate({
      productId: selectedProduct?.id || variantForm.product_id,
      data: variantData
    });
  };

  const handleQuickAddProduct = () => {
    if (!productForm.nome) {
      toast.error('Nome do produto é obrigatório');
      return;
    }

    const productData = {
      nome: productForm.nome,
      sku: productForm.sku || `PROD-${Date.now()}`,
      categoria_id: productForm.categoria_id ? parseInt(productForm.categoria_id) : null,
      marca_id: productForm.marca_id ? parseInt(productForm.marca_id) : null,
      unidade_id: productForm.unidade_id ? parseInt(productForm.unidade_id) : null,
      ativo: true
    };

    createProductMutation.mutate(productData);
  };

  const validateItems = (itemsList) => {
    const itemCount = itemsList.length;
    const totalAmount = itemsList.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0));
    }, 0);

    const isValid = itemCount > 0 && itemsList.every(item =>
      item.variant_id && item.quantity > 0 && item.unit_price >= 0
    );

    if (onValidationChange) {
      onValidationChange(isValid, itemCount, totalAmount);
    }
  };

  const getItemTotal = (item) => {
    return (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Itens da Despesa
        </label>
        {items.length > 0 && (
          <span className="text-xs text-gray-500">
            {items.length} item(ns)
          </span>
        )}
      </div>

      {!disabled && (
        <div className="space-y-2">
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowAutocomplete(true)}
                  placeholder="Buscar produto ou variante..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  disabled={disabled}
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-200 border-t-purple-600"></div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setShowQuickAdd(true); setQuickAddMode('product'); }}
                className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Novo</span>
              </button>
            </div>

            {showAutocomplete && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto divide-y divide-gray-100">
                {searchResults.map((variant, sIdx) => {
                  const itemKey = `${variant.variant_id || variant.id}_${variant.supplier_price_id || variant.fornecedor_id || sIdx}`;
                  return (
                    <button
                      key={itemKey}
                      type="button"
                      onClick={() => handleSelectProduct(variant)}
                      className="w-full px-4 py-3 text-left hover:bg-purple-50/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {variant.product_name || variant.nome || 'Produto sem nome'}
                          </p>

                          <p className="text-xs text-gray-500 mt-0.5">
                            <span className="font-mono font-medium text-gray-700">
                              SKU: {variant.variant_sku || variant.sku_variacao || variant.sku || 'N/A'}
                            </span>
                            {variant.variant_cor && <span> • Cor: {variant.variant_cor}</span>}
                            {variant.variant_medida && <span> • Medida: {variant.variant_medida}</span>}
                            {variant.variant_tamanho && !variant.variant_medida && <span> • {variant.variant_tamanho}</span>}
                            {variant.espessura_mm && <span> • {Math.round(parseFloat(variant.espessura_mm))}mm</span>}
                          </p>

                          {/* Linha de Fornecedor e Referência */}
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {variant.fornecedor_nome ? (
                              <>
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 shadow-2xs">
                                  <Building2 className="w-3 h-3 text-purple-600" />
                                  <span>{variant.fornecedor_nome}</span>
                                </span>
                                {variant.is_preferencial && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 shadow-2xs">
                                    <Star className="w-3 h-3 fill-current text-amber-500" />
                                    <span>Preferencial</span>
                                  </span>
                                )}
                                {variant.referencia_fornecedor && (
                                  <span className="text-[10px] text-gray-600 font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                    Ref: {variant.referencia_fornecedor}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                <Box className="w-3 h-3 text-gray-400" />
                                <span>Custo base geral (sem fornecedor vinculado)</span>
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-extrabold text-emerald-700">
                            {formatCurrency(variant.preco_custo)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Estoque: <strong className="text-gray-800 font-semibold">{variant.estoque_disponivel ?? variant.estoque_total ?? 0}</strong>
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {showAutocomplete && searchResults.length === 0 && searchTerm.length >= 2 && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm text-gray-600 text-center mb-3">Nenhum produto encontrado</p>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => { setQuickAddMode('variant'); setShowQuickAdd(true); }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Layers className="w-4 h-4" />
                  Criar Variante
                </button>
                <button
                  type="button"
                  onClick={() => { setQuickAddMode('product'); setShowQuickAdd(true); }}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Box className="w-4 h-4" />
                  Criar Produto
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Add Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {quickAddMode === 'product' ? 'Criar Produto Completo' : 'Criar Nova Variante'}
              </h3>
              <button
                onClick={() => { setShowQuickAdd(false); resetQuickAddForms(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {quickAddMode === 'product' && !selectedProduct ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Modo:</strong> Criar novo produto com variante
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Produto *
                      </label>
                      <input
                        type="text"
                        value={productForm.nome}
                        onChange={(e) => setProductForm(prev => ({ ...prev, nome: e.target.value }))}
                        placeholder="Ex: MDF MDP"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                      <input
                        type="text"
                        value={productForm.sku}
                        onChange={(e) => setProductForm(prev => ({ ...prev, sku: e.target.value }))}
                        placeholder="Código interno"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                      <select
                        value={productForm.categoria_id}
                        onChange={(e) => setProductForm(prev => ({ ...prev, categoria_id: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione</option>
                        {categoriesData?.data?.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nome || cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                      <select
                        value={productForm.marca_id}
                        onChange={(e) => setProductForm(prev => ({ ...prev, marca_id: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione</option>
                        {brandsData?.data?.map(brand => (
                          <option key={brand.id} value={brand.id}>{brand.nome || brand.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                      <select
                        value={productForm.unidade_id}
                        onChange={(e) => setProductForm(prev => ({ ...prev, unidade_id: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione</option>
                        {unitsData?.data?.map(unit => (
                          <option key={unit.id} value={unit.id}>{unit.nome || unit.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Dados da Variante</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SKU Variação</label>
                        <input
                          type="text"
                          value={variantForm.sku_variacao}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, sku_variacao: e.target.value }))}
                          placeholder="VAR-001"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cor</label>
                        <input
                          type="text"
                          value={variantForm.cor}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, cor: e.target.value }))}
                          placeholder="Branco"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tamanho</label>
                        <input
                          type="text"
                          value={variantForm.tamanho_label}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, tamanho_label: e.target.value }))}
                          placeholder="15mm"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Preço Custo</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variantForm.preco_custo}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, preco_custo: e.target.value }))}
                          placeholder="0.00"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : quickAddMode === 'variant' && (
                <>
                  {!selectedProduct ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Selecione o Produto</label>
                      <select
                        value={variantForm.product_id}
                        onChange={(e) => {
                          const product = productsData?.data?.find(p => p.id === parseInt(e.target.value));
                          setSelectedProduct(product);
                          setVariantForm(prev => ({ ...prev, product_id: e.target.value }));
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione um produto</option>
                        {productsData?.data?.map(product => (
                          <option key={product.id} value={product.id}>{product.nome || product.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Produto:</strong> {selectedProduct.nome || selectedProduct.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setSelectedProduct(null); setVariantForm(prev => ({ ...prev, product_id: '' })); }}
                        className="text-xs text-blue-600 hover:underline mt-1"
                      >
                        Trocar produto
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">SKU Variação *</label>
                      <input
                        type="text"
                        value={variantForm.sku_variacao}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, sku_variacao: e.target.value }))}
                        placeholder="VAR-001"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cor</label>
                      <input
                        type="text"
                        value={variantForm.cor}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, cor: e.target.value }))}
                        placeholder="Branco"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Tamanho/Rótulo</label>
                      <input
                        type="text"
                        value={variantForm.tamanho_label}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, tamanho_label: e.target.value }))}
                        placeholder="15mm"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Medida Texto</label>
                      <input
                        type="text"
                        value={variantForm.medida_text}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, medida_text: e.target.value }))}
                        placeholder="2750x1850x15mm"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Espessura (mm)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.espessura_mm}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, espessura_mm: e.target.value }))}
                        placeholder="15"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Comprimento (cm)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.comprimento_cm}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, comprimento_cm: e.target.value }))}
                        placeholder="275"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Largura (cm)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.largura_cm}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, largura_cm: e.target.value }))}
                        placeholder="185"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Preço Custo *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.preco_custo}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, preco_custo: e.target.value }))}
                        placeholder="0.00"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => { setShowQuickAdd(false); resetQuickAddForms(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={quickAddMode === 'product' ? handleQuickAddProduct : handleQuickAddVariant}
                disabled={createProductMutation.isPending || createVariantMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {createProductMutation.isPending || createVariantMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>Salvar e Adicionar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {items.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">Qtd</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Preço Unit.</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">Total</th>
                {!disabled && <th className="px-3 py-2 w-12"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.variant?.product_name || item.variant?.nome || item.product_name || 'Produto'}
                      </p>
                      <p className="text-xs text-gray-500">
                        SKU: {item.variant?.variant_sku || item.variant?.sku_variacao || item.variant_sku || 'N/A'}
                        {item.variant?.variant_cor && ` • ${item.variant.variant_cor}`}
                        {item.variant?.variant_medida && ` • ${item.variant.variant_medida}`}
                      </p>
                      {(item.supplier_name || item.variant?.fornecedor_nome) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                            <Building2 className="w-3 h-3 text-purple-600" />
                            <span>{item.supplier_name || item.variant?.fornecedor_nome}</span>
                          </span>
                          {(item.supplier_ref || item.variant?.referencia_fornecedor) && (
                            <span className="text-[11px] text-gray-600 font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                              Ref: {item.supplier_ref || item.variant?.referencia_fornecedor}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {disabled ? (
                      <span className="text-sm text-gray-900 text-center block">{item.quantity}</span>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {disabled ? (
                      <span className="text-sm text-gray-900 text-right block">{formatCurrency(item.unit_price)}</span>
                    ) : (
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">R$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handlePriceChange(index, e.target.value)}
                          className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(getItemTotal(item))}
                    </span>
                  </td>
                  {!disabled && (
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="3" className="px-3 py-2 text-right">
                  <span className="text-sm font-medium text-gray-700">Total:</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="text-base font-bold text-gray-900">
                    {formatCurrency(items.reduce((sum, item) => sum + getItemTotal(item), 0))}
                  </span>
                </td>
                {disabled && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
          <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-1">Nenhum item adicionado</p>
          <p className="text-xs text-gray-400">Busque e adicione produtos acima ou crie novos</p>
        </div>
      )}

      {errors?.items && (
        <div className="flex items-center gap-1 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{errors.items}</span>
        </div>
      )}
    </div>
  );
};

export default PurchaseItemsGrid;
